import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Church, Menu, X, Search, MapPin, Store, Newspaper, BookOpen } from "lucide-react";

/**
 * PublicLayout - A simplified layout for public (non-authenticated) pages
 * Shows a header with navigation to public pages and login/register CTAs
 */
export default function PublicLayout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const publicNavItems = [
    { path: "/sacrament-finder", label: "Sacrament Finder", icon: Search },
    { path: "/parishes", label: "Parishes", icon: Church },
    { path: "/marketplace", label: "Marketplace", icon: Store },
    { path: "/directory", label: "Business Directory", icon: MapPin },
    { path: "/vatican-news", label: "Vatican News", icon: Newspaper },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E0D5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[var(--liturgical-primary)] flex items-center justify-center">
                <Church className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-[#2D2A26] font-['Cinzel']">Parish</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {publicNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium font-['Nunito'] transition-colors ${
                    isActive(item.path)
                      ? "bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)]"
                      : "text-[#6B665E] hover:text-[#2D2A26] hover:bg-[#F9F7F2]"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="font-['Nunito'] text-[#6B665E] hover:text-[#2D2A26]">
                  Log In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
                  Join Parish
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-md text-[#6B665E] hover:bg-[#F9F7F2]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E5E0D5] bg-white">
            <div className="px-4 py-3 space-y-1">
              {publicNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium font-['Nunito'] ${
                    isActive(item.path)
                      ? "bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)]"
                      : "text-[#6B665E] hover:bg-[#F9F7F2]"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
              <div className="pt-3 border-t border-[#E5E0D5] mt-3 space-y-2">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full font-['Nunito']">
                    Log In
                  </Button>
                </Link>
                <Link to="/register" className="block">
                  <Button className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
                    Join Parish
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E5E0D5] mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[var(--liturgical-primary)] flex items-center justify-center">
                <Church className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm text-[#6B665E] font-['Nunito']">
                Parish App
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-[#6B665E] font-['Nunito']">
              <Link to="/privacy" className="hover:text-[#2D2A26]">Privacy</Link>
              <Link to="/terms" className="hover:text-[#2D2A26]">Terms</Link>
              <Link to="/community-guidelines" className="hover:text-[#2D2A26]">Guidelines</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
